import React, { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { Spin } from "antd";
import { ROUTER_FUTURE } from "../routerFuture";
import i18n from "../i18n";
import { App } from "../App";
import { initKeycloak, loginToApp } from "../keycloak";
import { ensureCountriesLoaded } from "../countries/countryUtils";
import { isPublicPath } from "./publicPaths";
import { AuthStatusProvider } from "./AuthStatus";
import { ProfileLocaleSync } from "./ProfileLocaleSync";

/**
 * US-806: public SPA paths render immediately; Keycloak check-sso runs in the background.
 * Protected paths still wait for init (then RequireAuth may call login).
 */
export function Bootstrap() {
  const publicRoute = isPublicPath();
  const [ready, setReady] = useState(publicRoute);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const boot = Promise.all([initKeycloak(), ensureCountriesLoaded().catch(() => undefined)]);

    boot
      .then(() => {
        if (cancelled) return;
        setAuthReady(true);
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Keycloak init failed:", err);
        if (publicRoute) {
          // Landing / share / supplier must not hard-fail when SSO is unreachable.
          setAuthReady(true);
          setReady(true);
          return;
        }
        const detail =
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : err?.error ?? err?.error_description;
        const hint = window.location.port === "3000" ? i18n.t("auth.localhostHint") : "";
        setError(
          detail
            ? i18n.t("auth.keycloakInitFailedDetail", { detail: `${detail}${hint}` })
            : `${i18n.t("auth.keycloakInitFailed")}${hint}`
        );
      });

    return () => {
      cancelled = true;
    };
  }, [publicRoute]);

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <p>{error}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button type="button" onClick={() => loginToApp(window.location.pathname || "/dds")}>
            {i18n.t("auth.login")}
          </button>
          <button type="button" onClick={() => window.location.reload()}>
            {i18n.t("auth.reload")}
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}
        data-testid="auth-bootstrap-loading"
      >
        <Spin size="large" tip={i18n.t("auth.authorizing")} />
      </div>
    );
  }

  return (
    <AuthStatusProvider authReady={authReady}>
      <ProfileLocaleSync />
      <BrowserRouter future={ROUTER_FUTURE}>
        <App />
      </BrowserRouter>
    </AuthStatusProvider>
  );
}
